import { canUseOnChannel } from './rights'
import type { CabinetConfigurationSnapshot } from './cabinet'
import type { ContentEntity, Product, SolutionDraft } from './types'

export function createSolutionDraft(
  contentVersion: string,
  now = new Date()
): SolutionDraft {
  return {
    contentVersion,
    customerLabel: '',
    projectLabel: '',
    createdAt: now.toISOString(),
    items: []
  }
}

export function addEntityToDraft(
  draft: SolutionDraft,
  entity: ContentEntity,
  now = new Date()
): SolutionDraft {
  if (draft.contentVersion !== entity.contentVersion) {
    throw new Error('内容版本已变化，请清空方案后重新选择')
  }
  if (!canUseOnChannel(entity, 'solution_preview', now)) {
    throw new Error('该内容未获准加入方案预览')
  }

  const nextItem = {
    itemId: entity.id,
    entityId: entity.id,
    contentType: entity.contentType,
    title: entity.title
  }

  if (entity.contentType === 'software-style') {
    const styleIndexes = draft.items.flatMap((item, index) =>
      item.contentType === 'software-style' ? [index] : []
    )
    const selectedStyle =
      styleIndexes.length === 1 ? draft.items[styleIndexes[0] ?? -1] : undefined

    if (selectedStyle?.entityId === entity.id) return draft

    if (styleIndexes.length > 0) {
      const firstStyleIndex = styleIndexes[0] ?? draft.items.length
      const items = draft.items.filter(
        (item) => item.contentType !== 'software-style'
      )
      items.splice(firstStyleIndex, 0, nextItem)
      return { ...draft, items }
    }
  }

  if (draft.items.some((item) => item.itemId === entity.id)) return draft

  return {
    ...draft,
    items: [...draft.items, nextItem]
  }
}

export function addCabinetConfigurationToDraft(
  draft: SolutionDraft,
  cabinet: Product,
  configuration: CabinetConfigurationSnapshot,
  now = new Date()
): SolutionDraft {
  if (cabinet.productCategory !== 'cabinet') {
    throw new Error('机柜配置必须关联机柜产品')
  }
  if (cabinet.id !== configuration.cabinetProfileId) {
    throw new Error('机柜配置与产品不匹配')
  }
  if (draft.contentVersion !== cabinet.contentVersion) {
    throw new Error('内容版本已变化，请清空方案后重新选择')
  }
  if (!canUseOnChannel(cabinet, 'solution_preview', now)) {
    throw new Error('该机柜未获准加入方案预览')
  }

  const itemId = [
    'cabinet-layout',
    configuration.cabinetProfileId,
    configuration.systemId,
    configuration.pointCount
  ].join(':')
  if (draft.items.some((item) => item.itemId === itemId)) return draft

  return {
    ...draft,
    items: [
      ...draft.items,
      {
        itemId,
        entityId: cabinet.id,
        contentType: cabinet.contentType,
        title: `${configuration.cabinetTitle} / ${configuration.systemLabel}`,
        configuration
      }
    ]
  }
}

export function removeEntityFromDraft(
  draft: SolutionDraft,
  itemId: string
): SolutionDraft {
  return {
    ...draft,
    items: draft.items.filter((item) => item.itemId !== itemId)
  }
}

export function moveDraftItem(
  draft: SolutionDraft,
  fromIndex: number,
  toIndex: number
): SolutionDraft {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= draft.items.length ||
    toIndex >= draft.items.length
  ) {
    return draft
  }
  if (fromIndex === toIndex) return draft

  const items = [...draft.items]
  const [item] = items.splice(fromIndex, 1)
  if (item === undefined) return draft
  items.splice(toIndex, 0, item)
  return { ...draft, items }
}

export function updateDraftLabels(
  draft: SolutionDraft,
  labels: Pick<SolutionDraft, 'customerLabel' | 'projectLabel'>
): SolutionDraft {
  return {
    ...draft,
    customerLabel: labels.customerLabel.trim(),
    projectLabel: labels.projectLabel.trim()
  }
}
